import Papa from 'papaparse';
import type { ReportTable } from '@erp/shared-types';
import { formatDateOnlyLocal } from '@erp/shared-utils';

export interface DownloadCSVOptions {
  data: Record<string, unknown>[];
  filename?: string;
  columns?: Array<{
    id: string;
    label: string;
  }>;
  includeTotals?: boolean; // Whether to include a totals row (default: true)
}

export interface MultiTableCSVOptions {
  tables: Array<{
    title: string;
    table: ReportTable;
    rowLabelHeader?: string; // Header for the first column (row labels), e.g., "Category", "Supplier"
    visibleColumnIndices?: number[]; // Optional: indices of columns to include (for time filtering)
    includeTotals?: boolean; // Whether to include a totals row for this table (default: true)
  }>;
  filename?: string;
}

/**
 * Downloads data as a CSV file
 * @param options - Configuration for the CSV download
 */
export function downloadCSV({
  data,
  filename = 'table-data',
  columns,
  includeTotals = true,
}: DownloadCSVOptions) {
  try {
    if (!data || data.length === 0) {
      console.warn('No data to download');
      return;
    }

    // If columns are provided, filter and rename the data
    let dataToDownload = data;
    if (columns && columns.length > 0) {
      dataToDownload = data.map((row) => {
        const filteredRow: Record<string, unknown> = {};
        columns.forEach((column) => {
          let cellValue = row[column.id];
          // Extract value from cell objects with formattedValue
          if (
            cellValue &&
            typeof cellValue === 'object' &&
            'value' in cellValue &&
            'formattedValue' in cellValue
          ) {
            // If value is 0 but formattedValue is a non-empty string that's not a currency/number format,
            // use formattedValue (e.g., for supplier codes stored as {value: 0, formattedValue: "ABC"})
            const formatted = (cellValue as { formattedValue: unknown })
              .formattedValue;
            if (
              cellValue.value === 0 &&
              typeof formatted === 'string' &&
              formatted !== '' &&
              formatted !== '-' &&
              formatted !== '$0.00' &&
              !formatted.startsWith('$')
            ) {
              cellValue = formatted;
            } else {
              cellValue = cellValue.value;
            }
          }
          filteredRow[column.label] = cellValue;
        });
        return filteredRow;
      });
    }

    // Add totals row if requested
    if (includeTotals && dataToDownload.length > 0) {
      const totalsRow: Record<string, unknown> = {};
      const firstColumnKey = Object.keys(dataToDownload[0])[0];

      // Set first column to "Total"
      totalsRow[firstColumnKey] = 'Total';

      // Calculate sums for numeric columns
      Object.keys(dataToDownload[0]).forEach((key, index) => {
        if (index === 0) return; // Skip first column (already set to "Total")

        let sum = 0;
        let hasNumericValues = false;

        dataToDownload.forEach((row) => {
          const value = row[key];
          if (typeof value === 'number' && !isNaN(value)) {
            sum += value;
            hasNumericValues = true;
          }
        });

        totalsRow[key] = hasNumericValues ? sum : '';
      });

      dataToDownload.push(totalsRow);
    }

    // Generate CSV content
    const csvContent = Papa.unparse(dataToDownload);

    // Create filename with timestamp
    const timestamp = new Date().toISOString().split('T')[0];
    const baseFilename = filename.replace(/\.[^/.]+$/, ''); // Remove extension if present
    const downloadFilename = `${baseFilename}-${timestamp}.csv`;

    // Create and trigger download
    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = downloadFilename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.warn('Error downloading CSV:', error);
  }
}

/**
 * Downloads multiple ReportTable objects as a single CSV file with stacked tables
 * @param options - Configuration for the multi-table CSV download
 */
export function downloadMultiTableCSV({
  tables,
  filename = 'report',
}: MultiTableCSVOptions) {
  try {
    if (!tables || tables.length === 0) {
      console.warn('No tables to download');
      return;
    }

    // Build CSV content manually to stack tables with section headers
    const csvRows: string[] = [];

    tables.forEach((tableSection, tableIndex) => {
      const {
        title,
        table,
        rowLabelHeader,
        visibleColumnIndices,
        includeTotals = true,
      } = tableSection;

      // Add blank line between tables (except before first table)
      if (tableIndex > 0) {
        csvRows.push('');
      }

      // Add table title as a section header
      csvRows.push(title);

      // Determine which columns to include
      const columnsToInclude =
        visibleColumnIndices ||
        Array.from({ length: table.columnHeaders.length }, (_, i) => i);

      // Add column headers
      const headers: string[] = [];

      // Add row label header as first column
      const labelHeader =
        rowLabelHeader ||
        (table.title.includes('Supplier') ? 'Supplier' : 'Category');
      headers.push(escapeCSVValue(labelHeader));

      // Add visible data column headers
      columnsToInclude.forEach((colIndex) => {
        headers.push(escapeCSVValue(table.columnHeaders[colIndex]));
      });
      csvRows.push(headers.join(','));

      // Track column totals if needed
      const columnTotals: number[] = new Array(columnsToInclude.length).fill(0);

      // Add data rows
      table.rows.forEach((row) => {
        const rowData: string[] = [escapeCSVValue(row.label)];

        // Add cell values for visible columns only
        columnsToInclude.forEach((colIndex, arrayIndex) => {
          const cell = row.cells[colIndex];
          if (cell) {
            // Use formattedValue if it's a non-numeric string (like supplier codes)
            if (
              cell.value === 0 &&
              typeof cell.formattedValue === 'string' &&
              cell.formattedValue !== '' &&
              cell.formattedValue !== '-' &&
              cell.formattedValue !== '$0.00' &&
              !cell.formattedValue.startsWith('$')
            ) {
              rowData.push(escapeCSVValue(cell.formattedValue));
              // Don't add to totals for non-numeric formatted values
            } else {
              // For numeric values, export the raw number
              rowData.push(escapeCSVValue(cell.value.toString()));
              // Add to column total
              if (includeTotals && typeof cell.value === 'number') {
                columnTotals[arrayIndex] += cell.value;
              }
            }
          } else {
            rowData.push('');
          }
        });

        csvRows.push(rowData.join(','));
      });

      // Add totals row if requested
      if (includeTotals && table.rows.length > 0) {
        const totalsRowData: string[] = [escapeCSVValue('Total')];
        columnTotals.forEach((total) => {
          totalsRowData.push(escapeCSVValue(total.toString()));
        });
        csvRows.push(totalsRowData.join(','));
      }
    });

    // Join all rows with newlines
    const csvContent = csvRows.join('\n');

    // Create filename with timestamp
    const timestamp = formatDateOnlyLocal(new Date());
    const baseFilename = filename.replace(/\.[^/.]+$/, ''); // Remove extension if present
    const downloadFilename = filename.toLowerCase().endsWith('.csv')
      ? filename
      : `${baseFilename}-${timestamp}.csv`;

    // Create and trigger download
    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = downloadFilename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.warn('Error downloading multi-table CSV:', error);
  }
}

/**
 * Escape CSV values for proper formatting
 */
function escapeCSVValue(value: string | number): string {
  const stringValue = String(value);
  // If value contains comma, quote, or newline, wrap in quotes and escape quotes
  if (
    stringValue.includes(',') ||
    stringValue.includes('"') ||
    stringValue.includes('\n')
  ) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
}
